import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  User, FileText, Upload, Bell, Settings, LogOut, 
  CheckCircle, Clock, AlertCircle, ChevronRight,
  Shield, Home
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, docsRes, statusRes] = await Promise.all([
        axios.get(`${API}/customer/profile`, { withCredentials: true }),
        axios.get(`${API}/customer/documents`, { withCredentials: true }),
        axios.get(`${API}/customer/status`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setDocuments(docsRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending Review' },
      in_review: { color: 'bg-blue-100 text-blue-800', icon: Clock, text: 'In Review' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Rejected' },
      documents_needed: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, text: 'Documents Needed' },
      not_started: { color: 'bg-slate-100 text-slate-800', icon: Clock, text: 'Not Started' }
    };
    const badge = badges[status] || badges.not_started;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurance-guide-8/artifacts/nhiwlosf_image.png" 
                alt="WealthConnexs"
                className="h-10"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-600 hover:text-slate-900">
                <Home className="w-5 h-5" />
              </Link>
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-navy rounded-2xl p-8 text-white mb-8"
        >
          <h1 className="text-2xl font-bold font-serif mb-2">Welcome to Your Portal</h1>
          <p className="text-slate-300">
            Manage your insurance applications, upload documents, and track your progress.
          </p>
        </motion.div>

        {/* Status Card */}
        {status && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 mb-8"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-navy mb-2">Application Status</h2>
                {status.application_id && (
                  <p className="text-sm text-slate-500 mb-2">ID: {status.application_id}</p>
                )}
                {getStatusBadge(status.status)}
              </div>
              {!profile?.full_legal_name && (
                <Button 
                  onClick={() => navigate('/portal/profile')}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                  data-testid="start-application-btn"
                >
                  Start Application
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>

            {/* Messages */}
            {status.messages?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Recent Updates</h3>
                <div className="space-y-2">
                  {status.messages.slice(0, 3).map((msg, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <Bell className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-700">{msg.message}</p>
                        <p className="text-slate-400 text-xs">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/portal/profile"
              className="block bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              data-testid="profile-card"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-navy mb-1">My Profile</h3>
              <p className="text-sm text-slate-500">
                {profile?.full_legal_name ? 'View and update your information' : 'Complete your application'}
              </p>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Link
              to="/portal/documents"
              className="block bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              data-testid="documents-card"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Upload className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-navy mb-1">Documents</h3>
              <p className="text-sm text-slate-500">
                {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
              </p>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Link
              to="/portal/settings"
              className="block bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              data-testid="settings-card"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Settings className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-navy mb-1">Settings</h3>
              <p className="text-sm text-slate-500">Security and account settings</p>
            </Link>
          </motion.div>
        </div>

        {/* Security Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-100 rounded-xl p-4 flex items-start gap-3"
        >
          <Shield className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-600">
            <p className="font-medium text-slate-700">Your data is secure</p>
            <p>All information is encrypted and protected. We never share your data with third parties without your consent.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
